import {
  model,
  Model,
  prop,
  idProp,
  registerRootStore,
  modelAction,
} from "mobx-keystone";
import { computed } from "mobx";
import { createContext } from "react";
import {
  CURRENCY_BY_CODE,
  CURRENCY_BY_SLUG,
  SATS_PER_BTC,
  CurrencyInfo,
} from "../helpers/currencies";
import {
  getDefaultLang,
  getAvailableLanguages,
  TRANSLATIONS,
  TranslationStrings,
  LANG_SLUGS,
  t,
} from "../helpers/translations";

export interface ConverterPageData {
  currency_code: string;
  btc_price_in_fiat: number;
  all_prices: Record<string, number>;
  locale?: string;
  /** amount pages (/…/1000-sats): pre-fill the converter with this many sats */
  initial_sats?: number;
  /** amount pages: share links point at the base page instead of this one */
  share_path?: string;
}

@model("exrails/SatoshiConverterStore")
export class SatoshiConverterStore extends Model({
  id: idProp,
  currencyCode: prop<string>("usd"),
  btcPriceInFiat: prop<number>(0),
  allPrices: prop<Record<string, number>>(() => ({})),
  satsInput: prop<string>(""),
  fiatInput: prop<string>(""),
  /** Which field the user last typed in: "sats" | "fiat" */
  lastEdited: prop<string>("sats"),
  /** Current display language — defaults to the currency's native language */
  locale: prop<string>(""),
  /** Path that share links (?satoshis=…) use; empty = the current page */
  sharePath: prop<string>(""),
}) {
  @modelAction
  hydrateFromPage = () => {
    const rootDiv = document.getElementById("root");
    if (!rootDiv?.dataset.hydrate) return;
    const data: ConverterPageData = JSON.parse(rootDiv.dataset.hydrate);
    this.currencyCode = data.currency_code;
    this.btcPriceInFiat = data.btc_price_in_fiat;
    this.allPrices = data.all_prices;
    // Use server-provided locale (from URL slug) or fall back to default
    this.locale = data.locale ?? getDefaultLang(this.currencyCode);
    this.sharePath = data.share_path ?? "";

    // Check URL params for pre-filled values
    const params = new URLSearchParams(window.location.search);
    const satsParam = params.get("satoshis");
    const fiatParam = params.get(this.currencyCode);

    if (satsParam && /^\d+$/.test(satsParam)) {
      this.setSatsInput(satsParam);
    } else if (fiatParam && /^[\d.]+$/.test(fiatParam)) {
      this.setFiatInput(fiatParam);
    } else if (data.initial_sats) {
      this.setSatsInput(String(data.initial_sats));
    }
  };

  @modelAction
  setSatsInput = (value: string) => {
    // Allow only digits
    const cleaned = value.replace(/[^0-9]/g, "");
    this.satsInput = cleaned;
    this.lastEdited = "sats";

    if (cleaned === "") {
      this.fiatInput = "";
      return;
    }

    const sats = parseInt(cleaned, 10);
    if (isNaN(sats)) {
      this.fiatInput = "";
      return;
    }

    const btcAmount = sats / SATS_PER_BTC;
    const fiatValue = btcAmount * this.btcPriceInFiat;
    this.fiatInput =
      fiatValue < 0.01 && fiatValue > 0
        ? fiatValue.toFixed(8).replace(/0+$/, "").replace(/\.$/, "")
        : fiatValue.toFixed(2);
    this.syncUrl();
  };

  @modelAction
  setFiatInput = (value: string) => {
    // Allow digits and decimal point
    const cleaned = value.replace(/[^0-9.]/g, "");
    // Prevent multiple decimal points
    const parts = cleaned.split(".");
    const sanitized =
      parts.length > 2 ? parts[0] + "." + parts.slice(1).join("") : cleaned;
    this.fiatInput = sanitized;
    this.lastEdited = "fiat";

    if (sanitized === "" || sanitized === ".") {
      this.satsInput = "";
      return;
    }

    const fiatVal = parseFloat(sanitized);
    if (isNaN(fiatVal) || this.btcPriceInFiat <= 0) {
      this.satsInput = "";
      return;
    }

    const btcAmount = fiatVal / this.btcPriceInFiat;
    const sats = Math.round(btcAmount * SATS_PER_BTC);
    this.satsInput = sats.toString();
    this.syncUrl();
  };

  @computed
  get currencyInfo(): CurrencyInfo | undefined {
    return CURRENCY_BY_CODE[this.currencyCode];
  }

  @computed
  get btcAmount(): string {
    const sats = parseInt(this.satsInput, 10);
    if (isNaN(sats) || sats === 0) return "0";
    const btc = sats / SATS_PER_BTC;
    return btc.toFixed(8).replace(/0+$/, "").replace(/\.$/, "");
  }

  @computed
  get formattedSats(): string {
    const sats = parseInt(this.satsInput, 10);
    if (isNaN(sats)) return "0";
    return sats.toLocaleString();
  }

  @computed
  get currencySymbol(): string {
    return this.currencyInfo?.symbol ?? this.currencyCode.toUpperCase();
  }

  @computed
  get currencyName(): string {
    return this.currencyInfo?.name ?? this.currencyCode.toUpperCase();
  }

  @computed
  get effectiveLocale(): string {
    return this.locale || getDefaultLang(this.currencyCode);
  }

  @computed
  get strings(): TranslationStrings {
    return (
      TRANSLATIONS[this.effectiveLocale]?.strings ?? TRANSLATIONS.en.strings
    );
  }

  /** All available non-English languages for this currency */
  @computed
  get availableLanguages(): string[] {
    return getAvailableLanguages(this.currencyCode);
  }

  /** The native (non-English) language for this currency, or null if English */
  @computed
  get nativeLang(): string | null {
    const lang = getDefaultLang(this.currencyCode);
    return lang !== "en" ? lang : null;
  }

  /** Whether the page has language options (at least one non-English language) */
  @computed
  get hasLanguageToggle(): boolean {
    return this.availableLanguages.length > 0;
  }

  /** The base path for this converter (no language suffix) */
  @computed
  get basePath(): string {
    return `/satoshis-to-${this.currencyCode}-converter`;
  }

  /** Get the permalink URL for a given locale */
  languageUrl(lang: string): string {
    if (lang === "en") return this.basePath;
    const slug = LANG_SLUGS[lang];
    return slug ? `${this.basePath}/${slug}` : this.basePath;
  }

  @modelAction
  toggleLocale = () => {
    if (this.effectiveLocale === "en" && this.nativeLang) {
      this.locale = this.nativeLang;
    } else {
      this.locale = "en";
    }
  };

  @computed
  get shareableUrl(): string {
    const base =
      window.location.origin + (this.sharePath || window.location.pathname);
    if (
      this.lastEdited === "sats" &&
      this.satsInput &&
      this.satsInput !== "0"
    ) {
      return `${base}?satoshis=${this.satsInput}`;
    }
    if (
      this.lastEdited === "fiat" &&
      this.fiatInput &&
      this.fiatInput !== "0"
    ) {
      return `${base}?${this.currencyCode}=${this.fiatInput}`;
    }
    return base;
  }

  syncUrl = () => {
    // an amount page keeps its own URL; its share link points at the base page
    if (this.sharePath && this.sharePath !== window.location.pathname) return;
    const url = this.shareableUrl;
    window.history.replaceState(null, "", url);
  };
}

const runCreateSatoshiConverterStore = () => {
  const store = new SatoshiConverterStore({});
  registerRootStore(store);
  return store;
};

const createdStore = runCreateSatoshiConverterStore();

export const SatoshiConverterStoreContext =
  createContext<SatoshiConverterStore>(createdStore);
